// src/pages/RuqyaPage/RuqyaPage.tsx
import React from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import InvocationCard from '../../components/InvocationCard/InvocationCard';
import Navigation from '../../components/Navigation/Navigation';
import ruqyaSorcelerieData from '../../data/ruqya-sorcellerie.json';
import './RuqyaPage.css';
import SEO from '../../components/SEO/SEO';

interface ContentItem {
  type: string;
  text?: string;
  arabic?: string;
  phonetic?: string;
  translation?: string;
  reference?: string;
  url?: string;
  number?: number;
  title?: string;
  content?: string;
  highlight?: string;
  conclusion?: string;
  mainMessage?: string;
  sources?: Array<{
    type: string;
    label: string;
    text: string;
    url: string;
  }>;
  items?: Array<{
    number: number;
    text: string;
  }>;
}

interface RuqyaData {
  title: string;
  author: string;
  description: string;
  content: ContentItem[];
}

const RuqyaPage: React.FC = () => {
  const data = ruqyaSorcelerieData as RuqyaData;

  // Scroll vers le haut quand la page se charge
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const renderContentItem = (item: ContentItem, index: number) => {
    switch (item.type) {
      case 'title':
        return <h1 key={index} className="ruqya-main-title">{item.text}</h1>;
      
      case 'subtitle':
        return <h2 key={index} className="ruqya-subtitle">{item.text}</h2>;
      
      case 'section-title':
        return <h3 key={index} className="ruqya-section-title">{item.text}</h3>;
      
      case 'paragraph':
        return (
          <div key={index} className="ruqya-paragraph">
            {item.text?.split('\n').map((line, lineIndex) => (
              <p key={lineIndex}>{line}</p>
            ))}
          </div>
        );
      
      case 'highlight':
        return (
          <div key={index} className="ruqya-highlight">
            {item.text?.split('\n').map((line, lineIndex) => (
              <p key={lineIndex}>{line}</p>
            ))}
          </div>
        );
      
      case 'enumeration':
        return (
          <div key={index} className="ruqya-enumeration">
            {item.text?.split('\n').map((line, lineIndex) => (
              <p key={lineIndex}>{line}</p>
            ))}
          </div>
        );
      
      case 'list-card':
        return (
          <div key={index} className="ruqya-list-card">
            {item.text?.split('\n\n').map((line, lineIndex) => {
              // Enlever les • et espaces du début si présents
              const cleanText = line.replace(/^•\s*/, '').trim();
              if (cleanText) {
                return (
                  <div key={lineIndex} className="ruqya-list-item">
                    <div className="ruqya-list-number">{lineIndex + 1}</div>
                    <div className="ruqya-list-text">{cleanText}</div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      
      case 'numbered-card':
        return (
          <InvocationCard
            key={index}
            id={item.number || 1}
            type="invocation"
            title={item.title || ''}
            arabicText={item.arabic || ''}
            phonetic={item.phonetic || ''}
            translation={item.translation || ''}
            context={item.content}
            showDownload={false}
          />
        );
      
      case 'verse-ref':
        return (
          <InvocationCard
            key={index}
            id={999} // Numéro spécial pour afficher un symbole
            type="invocation"
            title={item.title || ''}
            arabicText={item.arabic || ''}
            phonetic={item.phonetic || ''}
            translation={item.translation || ''}
            source={item.reference || ''}
            showDownload={false}
          />
        );
      
      case 'verse':
        return (
          <InvocationCard
            key={index}
            id={1}
            type="invocation"
            title=""
            arabicText={item.arabic || ''}
            phonetic={item.phonetic || ''}
            translation={item.translation || ''}
            source={item.reference || ''}
            showDownload={false}
          />
        );
      
      case 'invocation':
        return (
          <InvocationCard
            key={index}
            id={1}
            type="invocation"
            title=""
            arabicText={item.arabic || ''}
            phonetic={item.phonetic || ''}
            translation={item.translation || ''}
            showDownload={false}
          />
        );
      
      case 'separator':
        return <hr key={index} className="ruqya-separator" />;
      
      case 'source':
        return <div key={index} className="ruqya-source">{item.text}</div>;
      
      case 'link':
        return (
          <div key={index} className="ruqya-link">
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              {item.text}
            </a>
          </div>
        );
      
      case 'jujubier-card':
        return (
          <div key={index} className="ruqya-jujubier-card">
            <h3 className="ruqya-jujubier-title">{item.title}</h3>
            <div className="ruqya-jujubier-content">
              <p>{item.content}</p>
            </div>
            {item.highlight && (
              <div className="ruqya-jujubier-highlight">
                <p>{item.highlight}</p>
              </div>
            )}
            {item.conclusion && (
              <div className="ruqya-jujubier-conclusion">
                {item.conclusion.split('\n\n').map((paragraph, pIndex) => (
                  <p key={pIndex}>{paragraph}</p>
                ))}
              </div>
            )}
          </div>
        );
      
      case 'conclusion-card':
        return (
          <div key={index} className="ruqya-conclusion-card">
            <div className="ruqya-conclusion-header">
              <div className="ruqya-conclusion-icon">🤲</div>
              <h3 className="ruqya-conclusion-title">{item.title}</h3>
            </div>
            <div className="ruqya-conclusion-main">
              <p className="ruqya-conclusion-message">{item.mainMessage}</p>
            </div>
            <div className="ruqya-conclusion-content">
              {item.content?.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex}>{paragraph}</p>
              ))}
            </div>
          </div>
        );
      
      case 'sources-card':
        return (
          <div key={index} className="ruqya-sources-card">
            <div className="ruqya-sources-header">
              <div className="ruqya-sources-icon">📚</div>
              <h3 className="ruqya-sources-title">{item.title}</h3>
            </div>
            <div className="ruqya-sources-content">
              {item.sources?.map((source, sourceIndex) => {
                // Extraire l'ID de la vidéo YouTube si c'est une vidéo
                const getYouTubeVideoId = (url: string) => {
                  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
                  return match ? match[1] : null;
                };
                
                const videoId = source.type === 'video' ? getYouTubeVideoId(source.url) : null;
                
                if (source.type === 'video' && videoId) {
                  // Affichage avec vignette YouTube
                  return (
                    <div key={sourceIndex} className="ruqya-source-item ruqya-source-video">
                      <div className="ruqya-source-header">
                        <div className="ruqya-source-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23.498 6.186a2.97 2.97 0 0 0-2.089-2.089C19.636 3.698 12 3.698 12 3.698s-7.636 0-9.409.399A2.97 2.97 0 0 0 .502 6.186C.103 7.96.103 12 .103 12s0 4.04.399 5.814a2.97 2.97 0 0 0 2.089 2.089C4.364 20.302 12 20.302 12 20.302s7.636 0 9.409-.399a2.97 2.97 0 0 0 2.089-2.089C23.897 16.04 23.897 12 23.897 12s0-4.04-.399-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
                          </svg>
                        </div>
                        <div className="ruqya-source-text-info">
                          <div className="ruqya-source-label">{source.label}</div>
                          <div className="ruqya-source-text">{source.text}</div>
                        </div>
                      </div>
                      <div className="ruqya-video-thumbnail-container">
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="ruqya-video-thumbnail-link"
                        >
                          <div className="ruqya-video-thumbnail">
                            <img 
                              src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                              alt="Vignette de la vidéo YouTube"
                              onError={(e) => {
                                // Fallback vers une résolution plus faible si maxres n'existe pas
                                (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                              }}
                            />
                            <div className="ruqya-video-play-overlay">
                              <div className="ruqya-video-play-button">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5v14l11-7z" fill="white"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="ruqya-video-title">
                            Regarder la vidéo
                            <span className="ruqya-source-arrow">→</span>
                          </div>
                        </a>
                      </div>
                    </div>
                  );
                } else {
                  // Affichage normal pour les autres sources
                  return (
                    <div key={sourceIndex} className={`ruqya-source-item ruqya-source-${source.type}`}>
                      <div className="ruqya-source-icon">🏛️</div>
                      <div className="ruqya-source-info">
                        <div className="ruqya-source-label">{source.label}</div>
                        <div className="ruqya-source-text">{source.text}</div>
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="ruqya-source-link"
                        >
                          Consulter la fatwa
                          <span className="ruqya-source-arrow">→</span>
                        </a>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

    return (
    <div className="ruqya-page">
      <Navigation />
      
      <SEO 
        title="Ruqya contre la sorcellerie"
        description="Fatwa complète de Cheykh Ibn Bāz sur le traitement contre la sorcellerie (As-Sihr) selon le Coran et la Sunnah. Méthodes authentiques de Ruqya et invocations de protection."
        url="https://mahi-bayan.netlify.app/ruqya-sorcellerie"
      />
      
      <div className="ruqya-content">
        <Header 
          mainTitle={["L'IMPACT RÉEL DE LA SORCELLERIE (AS-SIHR)", "ET LA MANIÈRE D'EN GUÉRIR"]}
          subtitle="Fatwa de Cheykh Ibn Baz - رحمه الله - (Site officiel : https://binbaz.org.sa/fatwas/6919/حقيقة-وقوع-السحر-وكيفية-علاجه)"
        />

        <main className="ruqya-main">
          <div className="ruqya-content-wrapper">
            {data.content.map((item, index) => renderContentItem(item, index))}
          </div>
        </main>

        <Footer texts={[]} />
      </div>
    </div>
  );
};

export default RuqyaPage;
